import { c as _c } from "react/compiler-runtime";
import { useCallback, useReducer } from 'react';
import { FormDraft } from '../AdminFormsPage.model';
const MAX_UNDO_STEPS = 100;
const TEXT_EDIT_GROUP_MS = 750;
interface HistoryState {
  draft: FormDraft;
  past: string[];
  future: string[];
  group?: string;
  editedAt: number;
}
type HistoryAction = {
  type: 'edit';
  update: (draft: FormDraft) => FormDraft;
  group?: string;
  now: number;
} | {
  type: 'undo' | 'redo' | 'endGroup';
};
function snapshot(draft: FormDraft) {
  return JSON.stringify({
    draftConfig: draft.draftConfig
  });
}
function reducer(state: HistoryState, action: HistoryAction): HistoryState {
  if (action.type === 'endGroup') return {
    ...state,
    group: undefined
  };
  if (action.type === 'edit') {
    const draft = action.update(state.draft);
    const previous = snapshot(state.draft);
    if (snapshot(draft) === previous) {
      return {
        ...state,
        draft
      };
    }
    const grouped = action.group !== undefined && action.group === state.group && action.now - state.editedAt < TEXT_EDIT_GROUP_MS;
    return {
      draft,
      past: grouped ? state.past : [...state.past, previous].slice(-MAX_UNDO_STEPS),
      future: [],
      group: action.group,
      editedAt: action.now
    };
  }
  const source = action.type === 'undo' ? state.past : state.future;
  const serialized = source.at(-1);
  if (serialized === undefined) return state;
  const current = snapshot(state.draft);
  return {
    ...state,
    // Snapshots contain only configs serialized by this reducer.
    draft: {
      ...state.draft,
      ...JSON.parse(serialized)
    },
    past: action.type === 'undo' ? state.past.slice(0, -1) : [...state.past, current],
    future: action.type === 'redo' ? state.future.slice(0, -1) : [...state.future, current],
    group: undefined
  };
}

/** Tracks session-only JSON history. Other draft fields keep their current values. */
export function useFormDraftHistory(initialDraft) {
  const $ = _c(12);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== initialDraft) {
    t2 = {
      draft: initialDraft,
      past: t0,
      future: t1,
      editedAt: 0
    };
    $[2] = initialDraft;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [state, dispatch] = useReducer(reducer, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (update, group) => {
      dispatch({
        type: "edit",
        update,
        group,
        now: Date.now()
      });
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const setDraft = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => dispatch({
      type: "endGroup"
    });
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const endGroup = t4;
  const t5 = state.past.length > 0;
  const t6 = state.future.length > 0;
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => dispatch({
      type: "undo"
    });
    t8 = () => dispatch({
      type: "redo"
    });
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] !== state.draft || $[9] !== t5 || $[10] !== t6) {
    t9 = {
      draft: state.draft,
      setDraft,
      endGroup,
      canUndo: t5,
      canRedo: t6,
      undo: t7,
      redo: t8
    };
    $[8] = state.draft;
    $[9] = t5;
    $[10] = t6;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}